import React, { useEffect, useRef, useState } from 'react';
import {
  AtSign,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  Filter,
  RotateCcw,
  Search
} from 'lucide-react';
import useFeedProjects from '../components/feed/useFeedProjects';

// Keys match FEED_TYPES in the backend's post service.
export const FEED_TYPE_OPTIONS = [
  { value: 'post', label: 'Posts' },
  { value: 'lead', label: 'Leads' },
  { value: 'deal', label: 'Deals' },
  { value: 'quotation', label: 'Quotations & RFQs' },
  { value: 'inventory', label: 'Blocks, levels & units' },
  { value: 'task', label: 'Tasks' },
  { value: 'organisation', label: 'Company & people' },
  // Muster confirmed, new workmen / employees, payroll approved, bills certified.
  { value: 'workforce', label: 'Workforce' }
];

const DATE_OPTIONS = [
  { value: 'today', label: 'Today', days: 0 },
  { value: '7d', label: 'Last 7 days', days: 6 },
  { value: '30d', label: 'Last 30 days', days: 29 },
  { value: '90d', label: 'Last 90 days', days: 89 }
];

const Q_MAX = 100;

// `types` empty means every type. `range` is a quick range (relative to now);
// `from`/`to` are a custom range as local yyyy-mm-dd days, both inclusive.
// A quick range and a custom range never coexist.
export const EMPTY_FEED_FILTERS = {
  project_id: '',
  types: [],
  q: '',
  range: '',
  from: '',
  to: '',
  mentions: false
};

export const isFiltered = (filters) =>
  Boolean(
    filters.project_id ||
      filters.types?.length ||
      filters.q ||
      filters.range ||
      filters.from ||
      filters.to ||
      filters.mentions
  );

// ---- Local calendar days -------------------------------------------------

// 'yyyy-mm-dd' -> local midnight, or null for anything that isn't a real day
// (so 2026-02-30 is rejected rather than rolled into March).
const parseDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
};

const pad = (n) => String(n).padStart(2, '0');
const toDay = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatDay = (value) => {
  const date = parseDay(value);
  if (!date) return '';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() !== new Date().getFullYear() && { year: 'numeric' })
  });
};

const sortTypes = (types) =>
  FEED_TYPE_OPTIONS.map((option) => option.value).filter((value) => types.includes(value));

// The query the API takes. Days start at local midnight, so "Today" means the
// user's today, not UTC's. The API's `to` is exclusive, so a custom end day is
// sent as the start of the day after it.
export const toFeedQuery = (filters) => {
  const range = DATE_OPTIONS.find((option) => option.value === filters.range);
  let from;
  let to;
  if (range) {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - range.days);
    from = start.toISOString();
  } else {
    let start = parseDay(filters.from);
    let end = parseDay(filters.to);
    if (start && end && start > end) [start, end] = [end, start];
    if (start) from = start.toISOString();
    if (end) {
      end.setDate(end.getDate() + 1);
      to = end.toISOString();
    }
  }
  return {
    project_id: filters.project_id || undefined,
    types: filters.types?.length ? sortTypes(filters.types) : undefined,
    q: filters.q || undefined,
    from,
    to,
    mentions: filters.mentions || undefined
  };
};

// ---- URL <-> filters -----------------------------------------------------
// ?project=12&types=lead,deal&q=tower&from=2026-09-01&to=2026-09-15&mentions=1
// (or range=7d in place of from/to). Anything invalid is dropped.

const URL_KEYS = ['project', 'types', 'q', 'range', 'from', 'to', 'mentions'];

export const filtersFromSearchParams = (params) => {
  const project = params.get('project') || '';
  const range = params.get('range') || '';
  const validRange = DATE_OPTIONS.some((option) => option.value === range) ? range : '';
  let from = parseDay(params.get('from')) ? params.get('from') : '';
  let to = parseDay(params.get('to')) ? params.get('to') : '';
  if (validRange) {
    from = '';
    to = '';
  } else if (from && to && from > to) {
    [from, to] = [to, from];
  }
  const mentions = params.get('mentions');
  return {
    project_id: /^[\w-]{1,64}$/.test(project) ? project : '',
    types: sortTypes((params.get('types') || '').split(',').map((t) => t.trim())),
    q: (params.get('q') || '').trim().slice(0, Q_MAX),
    range: validRange,
    from,
    to,
    mentions: mentions === '1' || mentions === 'true'
  };
};

// Writes the filters over `base` (other params are kept), omitting empty ones.
export const filtersToSearchParams = (filters, base) => {
  const params = new URLSearchParams(base);
  URL_KEYS.forEach((key) => params.delete(key));
  if (filters.project_id) params.set('project', String(filters.project_id));
  if (filters.types?.length) params.set('types', sortTypes(filters.types).join(','));
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  if (filters.range) params.set('range', filters.range);
  else {
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
  }
  if (filters.mentions) params.set('mentions', '1');
  return params;
};

// A canonical string for comparing two sets of filters.
export const feedFiltersKey = (filters) => filtersToSearchParams(filters).toString();

// ---- Controls ------------------------------------------------------------

const pillClass = (active, className) =>
  `relative flex h-10 min-w-0 items-center rounded-control border bg-surface transition-colors hover:border-hairline-strong ${
    active ? 'border-hairline text-brand-800' : 'border-hairline text-gray-700'
  } ${className}`;

const PillIcon = ({ icon: Icon, active }) => (
  <span
    className={`pointer-events-none flex h-full w-10 shrink-0 items-center justify-center rounded-l-control ${
      active ? 'bg-brand-100 text-brand-700' : 'text-gray-500'
    }`}>
    <Icon size={16} />
  </span>
);

// A native select dressed as the pill in the design: keyboard and screen
// readers get the real control, the icon and chevron are decoration.
const SelectPill = ({ icon, value, onChange, label, children, className = '' }) => (
  <label className={`${pillClass(!!value, className)} focus-within:border-brand-600`}>
    <PillIcon icon={icon} active={!!value} />
    <span className="sr-only">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-full min-w-0 flex-1 cursor-pointer appearance-none truncate bg-transparent pl-2 pr-8 text-sm font-medium outline-none">
      {children}
    </select>
    <ChevronDown size={15} className="pointer-events-none absolute right-2.5 text-gray-500" />
  </label>
);

// The same pill as a button that opens a panel below it. Closes on an outside
// click or Escape.
const PopoverPill = ({ icon, label, text, active, className = '', panelClassName = '', children }) => {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const button = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        ref={button}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${label}: ${text}`}
        onClick={() => setOpen((value) => !value)}
        className={`${pillClass(active, 'w-full')} ${
          open ? 'border-brand-600' : ''
        } focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600`}>
        <PillIcon icon={icon} active={active} />
        <span className="min-w-0 flex-1 truncate pl-2 pr-8 text-left text-sm font-medium">{text}</span>
        <ChevronDown
          size={15}
          className={`pointer-events-none absolute right-2.5 text-gray-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <div
          role="dialog"
          aria-label={label}
          className={`absolute left-0 top-full z-30 mt-1.5 rounded-panel border border-hairline bg-surface p-1.5 shadow-overlay ${panelClassName}`}>
          {children}
        </div>
      )}
    </div>
  );
};

const OptionRow = ({ selected, onClick, children, role = 'menuitemcheckbox' }) => (
  <button
    type="button"
    role={role}
    aria-checked={selected}
    onClick={onClick}
    className={`flex w-full items-center gap-2.5 rounded-action px-2.5 py-2 text-left text-sm transition-colors hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600 ${
      selected ? 'font-medium text-brand-800' : 'text-gray-700'
    }`}>
    <span
      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
        selected ? 'border-brand-700 bg-brand-700 text-white' : 'border-hairline-strong bg-surface'
      }`}>
      {selected && <Check size={12} strokeWidth={3} />}
    </span>
    <span className="min-w-0 flex-1 truncate">{children}</span>
  </button>
);

const typesText = (types) => {
  if (!types.length) return 'All Activity Types';
  if (types.length === 1) {
    return FEED_TYPE_OPTIONS.find((option) => option.value === types[0])?.label || '1 type';
  }
  return `${types.length} activity types`;
};

const dateText = (filters) => {
  const range = DATE_OPTIONS.find((option) => option.value === filters.range);
  if (range) return range.label;
  if (filters.from && filters.to) {
    return filters.from === filters.to
      ? formatDay(filters.from)
      : `${formatDay(filters.from)} – ${formatDay(filters.to)}`;
  }
  if (filters.from) return `From ${formatDay(filters.from)}`;
  if (filters.to) return `Until ${formatDay(filters.to)}`;
  return 'Any date';
};

// Typing a year into a native date input passes through 0002, 0020, 0202...
// Those stay in the box as a draft; only a plausible day (or a cleared box)
// reaches the filters, so there is no request per keystroke.
const DateInput = ({ label, value, onChange, min, max }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const handleChange = (e) => {
    const next = e.target.value;
    setDraft(next);
    const usable = next === '' || (parseDay(next) && next >= '1970-01-01');
    if (usable && next !== value) onChange(next);
  };

  return (
    <label className="flex min-w-0 flex-1 flex-col gap-1 text-xs font-medium text-gray-600">
      {label}
      <input
        type="date"
        value={draft}
        min={min}
        max={max}
        onChange={handleChange}
        className="h-9 w-full min-w-0 rounded-control border border-hairline bg-surface px-2 text-sm text-gray-800 outline-none focus:border-brand-600"
      />
    </label>
  );
};

const LiveFeedFilters = ({ filters, onChange, actions }) => {
  const [search, setSearch] = useState(filters.q);

  const projectOptions = useFeedProjects();

  // Keep the box in step when filters are reset from outside.
  useEffect(() => {
    setSearch(filters.q);
  }, [filters.q]);

  // One request per pause in typing, not per keystroke. The ref holds the
  // latest filters so a debounced search doesn't undo a select changed mid-wait.
  const latest = useRef({ filters, onChange });
  latest.current = { filters, onChange };
  useEffect(() => {
    const value = search.trim();
    if (value === latest.current.filters.q) return undefined;
    const timer = setTimeout(
      () => latest.current.onChange({ ...latest.current.filters, q: value }),
      400
    );
    return () => clearTimeout(timer);
  }, [search]);

  const set = (key) => (value) => onChange({ ...filters, [key]: value });

  const types = filters.types || [];
  const toggleType = (value) =>
    onChange({
      ...filters,
      types: types.includes(value) ? types.filter((t) => t !== value) : sortTypes([...types, value])
    });

  const setRange = (range) => onChange({ ...filters, range, from: '', to: '' });
  const setDay = (key) => (value) => onChange({ ...filters, range: '', [key]: value });

  const today = toDay(new Date());
  const hasDate = Boolean(filters.range || filters.from || filters.to);

  return (
    <div className="mb-5 flex flex-wrap items-center gap-2.5 rounded-panel border border-hairline bg-surface p-2.5 shadow-sm">
      <button
        type="button"
        aria-pressed={!!filters.mentions}
        title="Posts where you were tagged, in the post or in a comment"
        onClick={() => onChange({ ...filters, mentions: !filters.mentions })}
        className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-control border px-3 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600 ${
          filters.mentions
            ? 'border-brand-600 bg-brand-100 text-brand-800'
            : 'border-hairline bg-surface text-gray-700 hover:border-hairline-strong'
        }`}>
        <AtSign size={16} className={filters.mentions ? 'text-brand-700' : 'text-gray-500'} />
        My mentions
      </button>

      <SelectPill
        icon={Building2}
        label="Project"
        value={filters.project_id}
        onChange={set('project_id')}
        className="w-full sm:w-52">
        <option value="">All Projects</option>
        {projectOptions.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </SelectPill>

      <PopoverPill
        icon={Filter}
        label="Activity type"
        text={typesText(types)}
        active={types.length > 0}
        className="w-full sm:w-52"
        panelClassName="w-full min-w-[15rem] sm:w-64">
        <div role="menu" aria-label="Activity types">
          <OptionRow selected={!types.length} onClick={() => onChange({ ...filters, types: [] })}>
            All Activity Types
          </OptionRow>
          <div className="my-1 border-t border-hairline" />
          {FEED_TYPE_OPTIONS.map((option) => (
            <OptionRow
              key={option.value}
              selected={types.includes(option.value)}
              onClick={() => toggleType(option.value)}>
              {option.label}
            </OptionRow>
          ))}
        </div>
      </PopoverPill>

      <label className="relative flex h-10 min-w-[12rem] flex-1 items-center rounded-control border border-hairline bg-surface-muted transition-colors focus-within:border-brand-600 focus-within:bg-surface">
        <Search size={16} className="pointer-events-none absolute left-3 text-gray-500" />
        <span className="sr-only">Search posts, people and projects</span>
        <input
          type="search"
          value={search}
          maxLength={Q_MAX}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search posts, people, projects…"
          className="h-full w-full bg-transparent pl-9 pr-3 text-sm outline-none placeholder:text-gray-400"
        />
      </label>

      <PopoverPill
        icon={CalendarDays}
        label="Date"
        text={dateText(filters)}
        active={hasDate}
        className="w-full sm:w-52"
        panelClassName="w-full min-w-[16rem] sm:left-auto sm:right-0 sm:w-72">
        <div role="radiogroup" aria-label="Date range">
          <OptionRow role="radio" selected={!hasDate} onClick={() => setRange('')}>
            Any date
          </OptionRow>
          {DATE_OPTIONS.map((option) => (
            <OptionRow
              key={option.value}
              role="radio"
              selected={filters.range === option.value}
              onClick={() => setRange(option.value)}>
              {option.label}
            </OptionRow>
          ))}
        </div>
        <div className="mt-1 border-t border-hairline px-2.5 pb-1.5 pt-2.5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Custom range
          </p>
          <div className="flex gap-2">
            <DateInput
              label="From"
              value={filters.from}
              max={filters.to || today}
              onChange={setDay('from')}
            />
            <DateInput
              label="To"
              value={filters.to}
              min={filters.from || undefined}
              max={today}
              onChange={setDay('to')}
            />
          </div>
        </div>
      </PopoverPill>

      {isFiltered(filters) && (
        <button
          type="button"
          onClick={() => onChange(EMPTY_FEED_FILTERS)}
          className="inline-flex h-10 items-center gap-1.5 rounded-control px-3 text-sm font-medium text-gray-600 hover:bg-surface-sunken hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-600">
          <RotateCcw size={15} />
          Clear
        </button>
      )}

      {actions}
    </div>
  );
};

export default LiveFeedFilters;
